import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, TrendingUp } from "lucide-react";
import { BusinessCard, BusinessCardCompact } from "@/components/BusinessCard";
import { analytics } from "@/utils/analytics";

const API_URL = '';

// Shuffle array for fair rotation within tier
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Fair sort: Featured first (shuffled), then standard (shuffled)
const fairSort = (businesses) => {
  const featured = businesses.filter(b => b.is_featured);
  const standard = businesses.filter(b => !b.is_featured);
  return [...shuffleArray(featured), ...shuffleArray(standard)];
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [businesses, setBusinesses] = useState([]);
  const [trendingBusinesses, setTrendingBusinesses] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Track category view
    analytics.trackCategoryView(slug);
    
    const fetchData = async () => {
      try {
        // Fetch category info
        const catResponse = await fetch(`${API_URL}/api/public/categories`);
        if (catResponse.ok) {
          const categories = await catResponse.json();
          const cat = categories.find(c => c.slug === slug);
          setCategory(cat);
        }

        // Fetch businesses - handle "all" as special case
        const url = slug === "all" 
          ? `${API_URL}/api/public/businesses`
          : `${API_URL}/api/public/businesses?category=${slug}`;
        
        const bizResponse = await fetch(url);
        if (bizResponse.ok) {
          const data = await bizResponse.json();
          
          // Fair rotation within tiers
          setBusinesses(fairSort(data));
          
          // Trending = shuffled selection for carousel (neutral editorial)
          setTrendingBusinesses(shuffleArray(data).slice(0, 5));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only refetch when slug changes; API_URL is a module constant
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const title = slug === "all" ? "All Places" : (category?.name || slug);

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-28" data-testid="category-page">
      {/* Compact Header - 56px height */}
      <header className="sticky top-0 z-40 glass-header h-14 px-5">
        <div className="max-w-lg mx-auto h-full flex items-center gap-3">
          <Link 
            to="/" 
            className="w-9 h-9 rounded-xl bg-slate-100/80 flex items-center justify-center nav-icon-glow"
            aria-label="Back to home" data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 text-slate-700" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-slate-900 truncate">
              {title}
            </h1>
            <p className="text-xs text-slate-500">
              {businesses.length} {businesses.length === 1 ? 'place' : 'places'}
            </p>
          </div>
        </div>
      </header>

      {/* Trending Carousel - Neutral editorial section */}
      {trendingBusinesses.length > 0 && (
        <section className="pt-6">
          <div className="max-w-lg mx-auto px-5 mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <h2 className="text-base font-semibold text-slate-900">
                Discover {title}
              </h2>
            </div>
          </div>
          
          <div 
            className="flex gap-3 px-5 overflow-x-auto pb-2 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {trendingBusinesses.map((business) => (
              <BusinessCardCompact key={`trending-${business.business_id}`} business={business} />
            ))}
            <div className="flex-shrink-0 w-2" />
          </div>
        </section>
      )}

      {/* Business List - Fair rotation */}
      <main className="px-5 pt-6">
        <div className="max-w-lg mx-auto">
          {businesses.length > 0 && (
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-4">
              All {title}
            </p>
          )}
          
          {businesses.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400">No places found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {businesses.map((business) => (
                <BusinessCard 
                  key={business.business_id} 
                  business={business}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
